import React, { useEffect, useRef, useState } from "react";
import SunEditor from "suneditor-react";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import ProductAPI from "../../../api/inventory/ProductApi";
import SEOFields from "../../../components/partials/SEOFields";
import slugify from "slugify";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import AiAPI from "../../../api/aiApi";

function Step4Description({ slug, nextStep, prevStep, productId }) {
  const productName = sessionStorage.getItem("product");

  if (!productId) {
    productId = sessionStorage.getItem("productId");
  }

  const [formData, setFormData] = useState({
    shortDescription: "",
    description: "",
    keyFeatures: [{ group: "", key: "", label: "", value: "" }],
  });

  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    tags: [],
  });

  const [shortDescShowPrompt, setShortDescShowPrompt] = useState(false);
  const [shortDescKeywords, setShortDescKeywords] = useState("");
  const [shortDescLoading, setShortDescLoading] = useState(false);

  const editorRef = useRef();
  const tooltipRef = useRef(null);

  const [errors, setErrors] = useState({});
  const { showLoader, hideLoader } = useLoader();

  const parseMarkdown = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/__(.*?)__/g, "<u>$1</u>")
      .replace(/\n/g, "<br>");
  };

  const typeWriterEffect = (markdownText) => {
    let i = 0;
    const finalHtml = parseMarkdown(markdownText);
    const instance = editorRef.current;
    if (!instance) return;
    instance.setContents("");
    const interval = setInterval(() => {
      const partial = markdownText
        .substring(0, i)
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
      instance.setContents(partial);
      i += 3;
      if (i > markdownText.length) {
        clearInterval(interval);
        instance.setContents(finalHtml);
      }
    }, 1);
  };

  // 🟢 SINGLE GENERATION API CALL FOR EVERYTHING (INCLUDING SEO)
  const generateAllContentViaAI = async () => {
    setShortDescShowPrompt(false);
    setShortDescLoading(true);

    let prompt = `You are a professional e-commerce copywriter and SEO expert. Generate full product details and high-ranking SEO metadata for the product: "${productName}".

    Requirements for each field:
    1. shortDescription: An engaging summary under 250 characters, optimized for grids. No markdown.
    2. description: Comprehensive and detailed long product description. It MUST be between 700 to 1000 words (or at least 4000-5000 characters) to ensure depth. Write in safe paragraph formats, highlighting core benefits, target audience, and use cases. Do NOT use markdown symbols like #, ##, *, **.
    3. keyFeatures: Extract 5–7 long key features as an array of objects matching this exact pattern:
       { "group": "Group Title", "key": "snake_case_key", "label": "Feature Label", "value": "Feature Value" }
    4. metaTitle: A high-CTR SEO friendly title under 60 characters.
    5. metaDescription: A compelling SEO description under 160 characters containing search intent hooks.
    6. tags: A simple array of 5-8 highly relevant e-commerce search keywords/tags strings.
    `;

    if (shortDescKeywords && shortDescKeywords.trim().length > 0) {
      prompt += `\nFocus heavily on incorporating the following custom user keywords: ${shortDescKeywords}.`;
    }

    prompt += `\n\nRespond with ONLY a valid, minified raw JSON object matching the schema below. Do not wrap it in markdown block quotes (like \`\`\`json).
    Schema:
    {
      "shortDescription": "...",
      "description": "...",
      "keyFeatures": [ { "group": "", "key": "", "label": "", "value": "" } ],
      "metaTitle": "...",
      "metaDescription": "...",
      "tags": ["tag1", "tag2"]
    }`;

    try {
      const result = await AiAPI.getContent({ prompt });

      let cleanJsonString = result.data.trim();
      if (cleanJsonString.startsWith("```json")) {
        cleanJsonString = cleanJsonString
          .replace(/^```json/, "")
          .replace(/```$/, "")
          .trim();
      } else if (cleanJsonString.startsWith("```")) {
        cleanJsonString = cleanJsonString
          .replace(/^```/, "")
          .replace(/```$/, "")
          .trim();
      }

      const parsedData = JSON.parse(cleanJsonString);

      // Set Main Product Data
      setFormData((prev) => ({
        ...prev,
        shortDescription: parsedData?.shortDescription || "",
        keyFeatures: Array.isArray(parsedData?.keyFeatures)
          ? parsedData.keyFeatures
          : prev.keyFeatures,
      }));

      // Set SEO Data Dynamically
      setSeoData({
        metaTitle: parsedData?.metaTitle || "",
        metaDescription: parsedData?.metaDescription || "",
        tags: Array.isArray(parsedData?.tags) ? parsedData.tags : [],
      });

      // Handle Editor Content Dynamic Typewriter Effect
      if (parsedData?.description) {
        setFormData((prev) => ({
          ...prev,
          description: parsedData.description,
        }));
        typeWriterEffect(parsedData.description);
      }

      showSuccess(
        "All product information and SEO tags synced successfully! ✨",
      );
    } catch (err) {
      console.error("Parsing breakdown:", err);
      showError("Failed to parse integrated AI response. Please try again.");
    } finally {
      setShortDescLoading(false);
    }
  };

  const slugifyKey = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_");
  };

  const handleFeatureChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.keyFeatures];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === "label" && {
          key: slugifyKey(value),
        }),
      };
      return { ...prev, keyFeatures: updated };
    });
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      keyFeatures: [
        ...prev.keyFeatures,
        { group: "", key: "", label: "", value: "" },
      ],
    }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      keyFeatures: prev.keyFeatures.filter((_, i) => i !== index),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({ ...prev, description: content }));
  };

  useEffect(() => {
    fetchStep4Description();
  }, []);

  const fetchStep4Description = async () => {
    try {
      const res = await ProductAPI.fetchStep4Description(productId);
      if (res.code === 200) {
        const result = res.data;
        setFormData({
          shortDescription: result.shortDescription,
          description: result.description,
          keyFeatures: result.keyFeatures,
        });

        setSeoData({
          metaTitle: result.meta.title || "",
          metaDescription: result.meta.description || "",
          tags: result.meta.keywords || [],
        });
      }
    } catch (err) {}
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = "Short Description is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader();

    if (!validate()) {
      hideLoader();
      return;
    }

    try {
      const payload = {
        ...formData,
        metaTitle: seoData.metaTitle,
        metaDescription: seoData.metaDescription,
        tags: seoData.tags,
        productId,
      };

      const res = await ProductAPI.updateStep4Description(productId, payload);

      if (res.code === 200) {
        showSuccess(res.message);
        nextStep(payload);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      {/* 🟢 BUTTON MOVED EXACTLY IN FRONT OF HEADING WITH TOOLTIP KEYWORDS BOX */}
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Description & SEO </h4>

        <div style={{ position: "relative", display: "inline-block" }}>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={(e) => setShortDescShowPrompt(true)}
            disabled={shortDescLoading}
          >
            {shortDescLoading ? "Generating Data..." : `Generate using AI ✨`}
          </button>

          {shortDescShowPrompt && (
            <div
              ref={tooltipRef}
              className="tooltipRef"
              style={{
                position: "absolute",
                right: 0,
                top: "35px",
                zIndex: 1050,
                background: "#fff",
                padding: "15px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                width: "300px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <div>
                <label style={{ fontWeight: "bold", color: "#333" }}>
                  Optional Keywords / Hints
                </label>
                <input
                  type="text"
                  className="form-control my-2"
                  placeholder="Enter comma-separated keywords"
                  value={shortDescKeywords}
                  onChange={(e) => setShortDescKeywords(e.target.value)}
                />
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-sm btn-light me-2"
                    onClick={(e) => {
                      e.preventDefault();
                      setShortDescShowPrompt(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      generateAllContentViaAI();
                    }}
                    disabled={shortDescLoading}
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Key Features about Product
              </label>
              {formData.keyFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="mt-1 row g-2 align-items-center"
                >
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Feature Group (e.g., Display)"
                      value={feature.group}
                      onChange={(e) =>
                        handleFeatureChange(index, "group", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Feature Label (e.g., Material)"
                      value={feature.label}
                      onChange={(e) =>
                        handleFeatureChange(index, "label", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Feature Value (e.g., Cotton)"
                      value={feature.value}
                      onChange={(e) =>
                        handleFeatureChange(index, "value", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-1 text-end">
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removeFeature(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}

              <div className="mt-2 mb-1">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addFeature}
                >
                  + Add Key Feature
                </button>
              </div>
            </div>
          </div>

          {/* Short Description Section */}
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Short Description</label>
              <textarea
                style={{ height: "100px" }}
                className="form-control"
                placeholder="Enter Short Description"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
              ></textarea>
            </div>
          </div>

          {/* SunEditor Rich Text Section */}
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Description</label>
              <SunEditor
                getSunEditorInstance={(sunEditor) => {
                  editorRef.current = sunEditor;
                }}
                height="250px"
                setOptions={{
                  buttonList: [
                    ["undo", "redo"],
                    ["font", "fontSize"],
                    ["formatBlock"],
                    ["bold", "underline", "italic", "strike"],
                    ["fontColor", "hiliteColor"],
                    ["align", "list", "table"],
                    ["link", "image", "video"],
                    ["fullScreen", "preview", "print"],
                  ],
                  imageFileInput: true,
                  font: [
                    "Arial",
                    "Comic Sans MS",
                    "Courier New",
                    "Georgia",
                    "Tahoma",
                    "Trebuchet MS",
                    "Verdana",
                  ],
                  fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36],
                }}
                setContents={formData.description || ""}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          {/* SEO Dynamic Fields Connection */}
          <SEOFields
            seoData={seoData}
            setSeoData={setSeoData}
            slug={slugify(sessionStorage.getItem("product") || "", {
              lower: true,
            })}
            errors={errors}
          />

          {/* Control Triggers */}
          <div className="row mt-3">
            <div className="col-md-12">
              <button
                type="submit"
                id="submitBtn"
                className="me-2 btn btn-primary shadow-none"
                onClick={prevStep}
              >
                Prev
              </button>
              <button
                type="submit"
                id="submitBtn"
                className="btn btn-primary shadow-none"
              >
                Next
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default Step4Description;
